<template>
  <v-card>
    <v-card-title> Estate Costs </v-card-title>
    <v-card-text>
      <p v-if="!testator.estateCostCalculation">
        Testator has not completed the estate cost calculator
      </p>
      <div v-else>
        <h2 class="mb-3">Inputs</h2>
        <div v-if="testator.properties.length" class="mb-7">
          <h3 class="mb-2">Properties</h3>
          <ul>
            <li
              v-for="property in testator.properties"
              :key="'EstateCalcProperty' + property.id"
            >
              <b>{{ property.physicalAddress }}</b>
              <ul>
                <li>Ownership: {{ propertyOwnership(property) }}</li>
                <li>
                  Property value:
                  <span v-if="property.marketValue"
                    >R {{ property.marketValue | currency }}</span
                  >
                  <span v-else>Unknown</span>
                </li>
                <li v-if="property.marketValue">
                  Ownership value:
                  <span>R {{ ownershipValue(property) | currency }}</span>
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Total property value</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.totalPropertyValue | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Value of your assets, other than property</p>
          </v-col>
          <v-col cols="12" sm="4">
            R
            {{
              testator.estateCostCalculation.nonPropertyAssetValue | currency
            }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Gross estate value</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.grossEstateValue | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Deemed assets</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.deemedAssetsValue | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Value of all assets left to spouse/partner</p>
          </v-col>
          <v-col cols="12" sm="4">
            R
            {{
              testator.estateCostCalculation.assetValueLeftToSpouse | currency
            }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Liabilities in the estate</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.totalLiabilities | currency }}
          </v-col>
        </v-row>

        <h2 class="mt-5 mb-3">Outputs</h2>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Executor fees</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.executorFees | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Conveyancing fees</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.conveyancingFees | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8">
            <p>Property ownership value</p>
          </v-col>
          <v-col cols="12" sm="4">
            R
            {{
              testator.estateCostCalculation.propertyOwnershipValue | currency
            }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Masters fees</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.mastersFees | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Standard administrative costs</p>
          </v-col>
          <v-col cols="12" sm="4">
            R
            {{
              testator.estateCostCalculation.standardAdministrativeCosts
                | currency
            }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Estate duty</p>
          </v-col>
          <v-col cols="12" sm="4">
            R {{ testator.estateCostCalculation.estateDuty | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8">
            <p>Dutiable estate before 4A deduction</p>
          </v-col>
          <v-col cols="12" sm="4">
            R
            {{
              testator.estateCostCalculation.dutiableEstateBefore4aDeduction
                | currency
            }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item font-weight-bold">
          <v-col cols="12" sm="8">
            <p>Total estimated estate administration costs</p>
          </v-col>
          <v-col cols="12" sm="4">
            R
            {{
              testator.estateCostCalculation.estimatedEstateAdministrativeCosts
                | currency
            }}
          </v-col>
        </v-row>

        <p class="mt-10 mb-2">
          The values above are based on the last time the testator completed the
          estate cost calculator ({{
            testator.estateCostCalculation.lastCalculationTime | date_DMMMYYYY
          }})
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
  export default {
    name: 'EstateCostsCard',
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    methods: {
      propertyOwnership(property) {
        switch (property.ownership) {
          case 'SOLE_OWN':
            return 'Sole owner';
          case 'CO_OWN_WITH_SPOUSE':
            return 'Co-own with spouse - ' + property.percentageShare + '%';
          case 'CO_OWN':
            return 'Co-own - ' + property.percentageShare + '%';
          case 'SPOUSE_SOLE_OWNER':
            return 'Spouse is the sole owner';
          default:
            return 'Unknown';
        }
      },
      ownershipValue(property) {
        switch (property.ownership) {
          case 'SOLE_OWN':
            return property.marketValue;
          case 'CO_OWN':
            return parseInt(
              property.marketValue * (property.percentageShare / 100)
            );
          case 'CO_OWN_WITH_SPOUSE':
            return parseInt(
              property.marketValue * (property.percentageShare / 100)
            );
          case 'SPOUSE_SOLE_OWNER':
            return 0;
          default:
            return property.marketValue;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .estate-cost-calc-line-item {
    margin-top: 6px;
  }

  p {
    margin: 0;
  }
</style>
